import { CurrentSearch } from './actionTypes';
import { TripType, FlightClass, DpPaxRestrictionPropsDefault, NonPrimeHotelPropsDefault, searcherConfigs } from '../../models';
import { getResidentDiscountProps, isFlightValidSearch } from './selectors';
import { DynpackRoomUtils, PreSearchUtils, DateUtils, HotelRoomUtils } from '../../utils';
import { SearchService } from '../../services';

const formatSearchData = (search) => {
    let segments = [];
    if (search.segments.length === 0) {
        segments = [{ id: Date.now() }];
        if (search.tripType !== TripType.ONE_WAY) {
            segments.push({ id: Date.now() + 1 });
        }
    } else {
        segments = search.segments.map((segment, i) => {
            return segment.id ? segment : { ...segment, id: Date.now() + i };
        });
        if (search.tripType === TripType.MULTIPLE && search.segments.length === 1) {
            segments.push({ id: Date.now() + 1 });
            if (search.segments[0].destination) {
                segments[1].origin = segments[0].destination;
            }
        }
    }
    return { ...search, segments };
};

const initialState = {
    tripType: TripType.ROUND_TRIP,
    isResident: false,
    segments: [
        { id: Date.now() },
        { id: Date.now() + 1 },
    ],
    rooms: [HotelRoomUtils.getDefaultRoom(DpPaxRestrictionPropsDefault)],
    nonPrimeHotelRooms: {
        rooms: NonPrimeHotelPropsDefault.minRooms,
        adults: NonPrimeHotelPropsDefault.minAdults,
        kids: [],
    },
    pax: {
        adults: 1,
        kids: 0,
        infants: 0,
    },
    isDirect: false,
    isHotel: false,
    airlineFilter: {
        enabled: false,
        airlineIata: '',
        airlineName: '',
    },
    cabinClass: FlightClass.TOURIST,
    separateStay: {
        enabled: false,
    },
    lastKnownOrigin: null,
    config: searcherConfigs.default,
};

const addSegment = (state, { segment }) => {
    const { destination: prevSegmentDestination } = state.segments[state.segments.length - 1];
    const isPrevSegmentDestinationINSP = prevSegmentDestination?.geoNodeId === 0;
    const isLastSegment = state.segments.length === state.config?.maxSegments[state.tripType] - 1;
    const shouldAutofillLastDestination = state.config?.autofillLastDestination && isLastSegment;
    if (state.tripType === TripType.ONE_WAY && isPrevSegmentDestinationINSP) {
        return {
            ...state,
            isResident: false,
            segments: [{ ...state.segments[0], destination: null }, segment],
        };
    }
    return {
        ...state,
        isResident: false,
        segments: [
            ...state.segments,
            {
                ...segment,
                origin: prevSegmentDestination,
                destination: shouldAutofillLastDestination ? state.segments[0].origin : null
            }
        ],
    };
};

const deleteSegment = (state, { index }) => {
    return {
        ...state,
        segments: [
            ...state.segments.slice(0, index),
            ...state.segments.slice(index + 1),
        ],
    };
};

const updateIsResident = (state, store) => {
    const { show } = getResidentDiscountProps({ ...store, currentSearch: state });
    return {
        ...state,
        isResident: state.isResident && show,
    };
};

const updateSeparateStay = (state) => {
    if (state.separateStay.enabled) {
        const enabled =
            DateUtils.isValidDate(DateUtils.stringToDate(state.segments[0].date)) &&
            DateUtils.isValidDate(DateUtils.stringToDate(state.segments[1].date)) &&
            DateUtils.compare(state.segments[0].date, state.segments[1].date) !== 0;
        let checkInDate = null;
        let checkOutDate = null;
        if (enabled) {
            checkInDate = DynpackRoomUtils.getSeparateStayValidDate(
                state.separateStay.checkInDate,
                state.segments[0].date,
                -1,
            );
            checkOutDate = DynpackRoomUtils.getSeparateStayValidDate(
                state.separateStay.checkOutDate,
                state.segments[1].date,
                1,
            );
        }
        const separateStay = { enabled, checkInDate, checkOutDate };
        return setSeparateStay(state, { separateStay });
    }
    return state;
};

const updateSegment = (state, { segment: newSegment, id }, store) => {
    const index = state.segments.findIndex((s) => s.id === id);
    const wasFirstOriginChangedWithNewIata = index === 0 && newSegment.origin?.iata;

    const segments = state.segments.map((prevSegment, i) => {
        const lastSegmentIndex = state.config?.maxSegments[state.tripType] - 1;

        if (i === index) {
            return { ...prevSegment, ...newSegment };
        } else if (i === index + 1) {
            if (state.tripType === TripType.ROUND_TRIP) {
                return { ...prevSegment, origin: newSegment.destination, destination: newSegment.origin };
            } else if (!prevSegment.origin) {
                return { ...prevSegment, origin: newSegment.destination };
            }
        }
        // keep first origin and last destination synced, unless user has already interacted with the destination
        if (i === lastSegmentIndex && state.config.autofillLastDestination && wasFirstOriginChangedWithNewIata) {
            const wasFirstOriginEqualToLastDestination = state.lastKnownOrigin?.iata === state.segments[lastSegmentIndex]?.destination?.iata;
            if (wasFirstOriginEqualToLastDestination) {
                return { ...prevSegment, destination: newSegment.origin };
            }
        }
        return prevSegment;
    });

    const newState = updateSeparateStay(
        updateIsResident(
            {
                ...state,
                segments,
            },
            store,
        ),
    );

    return wasFirstOriginChangedWithNewIata ? updateLastKnownOrigin(newState) : newState;
};

const getRoomErrors = (rooms, showErrors) => {
    return rooms.map((room) => ({
        ...room,
        showKidError: showErrors && HotelRoomUtils.isKidsAgeInvalid(room),
    }));
};

const getNonPrimeHotelRoomError = (nonPrimeHotelRoom, showErrors) => {
    return {
        ...nonPrimeHotelRoom,
        showKidError: showErrors && DynpackRoomUtils.isNonPrimeHotelRoomKidsAgeInvalid(nonPrimeHotelRoom),
    };
};

const setShowErrors = (state, { showErrors = true } = {}) => {
    const errors = {
        ...state,
        segments: state.segments.map((segment) => ({
            ...segment,
            showErrors,
        })),
    };

    if (state.isHotel) {
        return {
            ...errors,
            rooms: getRoomErrors(state.rooms, showErrors),
        };
    }

    return errors;
};

const setNonPrimeHotelShowErrors = (state, { showErrors = true } = {}) => {
    return {
        ...state,
        nonPrimeHotelRooms: getNonPrimeHotelRoomError(state.nonPrimeHotelRooms, showErrors),
    };
};

const setShowRoomErrors = (state, { showRoomErrors = true } = {})=> {
    return {
        ...state,
        rooms: getRoomErrors(state.rooms, showRoomErrors),
        nonPrimeHotelRooms: getNonPrimeHotelRoomError(state.nonPrimeHotelRooms, showRoomErrors),
    };
};

const resetSearch = (state) => {
    return {
        ...state,
        segments: state.segments.map((segment) => ({ ...segment, showErrors: false })),
        rooms: state.isHotel ? state.rooms.map((room) => ({ ...room, showKidError: false })) : [],
    };
};

const setAllSearch = (state, { search }) => {
    return {
        ...formatSearchData(search),
        nonPrimeHotelRooms: { ...state.nonPrimeHotelRooms },
        rooms: [...state.rooms],
        config: searcherConfigs.default,
    };
};

const setCabinClass = (state, { cabinClass }) => ({ ...state, cabinClass });

const setHotel = (state, { isHotel }) => ({ ...state, isHotel });

const setPax = (state, { pax }) => {
    if (pax.kids + pax.infants > 0) {
        return { ...state, pax, isResident: false };
    }
    return { ...state, pax };
};

const addRoom = (state, { newRoom }) => {
    let newRooms = Object.assign([], state.rooms);
    newRooms.push(newRoom);
    return { ...state, rooms: newRooms.sort((roomA, roomB) => roomA.id - roomB.id) };
};

const updateRoom = (state, { room }) => {
    const newRooms = state.rooms.filter((item) => item.id !== room.id);
    newRooms.push(room);
    return { ...state, rooms: newRooms.sort((roomA, roomB) => roomA.id - roomB.id) };
};

const deleteRoom = (state, { room }) => {
    let newRooms = state.rooms.filter((item) => item.id !== room.id);
    newRooms.sort((roomA, roomB) => roomA.id - roomB.id);
    return { ...state, rooms: newRooms };
};

const setHotelTripType = (state, { tripType }) => {
    let newSegments = state.segments;
    if (newSegments.length === 1 && tripType === TripType.ROUND_TRIP) {
        newSegments = [
            ...newSegments,
            {
                id: Date.now(),
                origin: newSegments[0].destination,
                destination: newSegments[0].origin,
            },
        ];
    }
    return setShowErrors({ ...state, tripType, segments: newSegments }, { showErrors: false });
};

const setTripType = (state, { tripType }) => {
    let newSegments = state.segments.slice(0, 1);
    if (tripType === TripType.ROUND_TRIP) {
        newSegments = [
            ...newSegments,
            {
                id: Date.now(),
                origin: newSegments[0].destination,
                destination: newSegments[0].origin,
            },
        ];
    } else if (tripType === TripType.MULTIPLE) {
        const { destination } = newSegments[0];
        const isDestinationINSP = destination && destination.geoNodeId === 0;
        if (isDestinationINSP) {
            newSegments = [{ ...newSegments[0], destination: null }, { id: Date.now() }];
        } else {
            newSegments = [...newSegments, { id: Date.now(), origin: destination }];
        }
    }
    return setShowErrors({ ...state, tripType, segments: newSegments }, { showErrors: false });
};

const setChainFilter = (state, { chain }) => ({ ...state, chainFilter: chain });

const setOnlyDirectFlights = (state, { isDirect }) => ({ ...state, isDirect });

const setAirlineFilter = (state, { airlineFilter }) => ({ ...state, airlineFilter });

const setResidentDiscount = (state, { isResident }) => ({ ...state, isResident });

const setSeparateStay = (state, { separateStay }) => {
    let newSeparateStay = { enabled: false };
    if (separateStay.enabled) {
        const checkInDate = DateUtils.isValidDate(separateStay.checkInDate) ? separateStay.checkInDate : state.segments[0].date;
        const checkOutDate = DateUtils.isValidDate(separateStay.checkOutDate) ? separateStay.checkOutDate : state.segments[1].date;
        newSeparateStay = { enabled: true, checkInDate, checkOutDate };
    }
    return { ...state, separateStay: newSeparateStay };
};

const handlePreSearch = (state) => {
    if (isFlightValidSearch({ currentSearch: state }, { currentSearch: state })
        && window['Odigeo']
        && window['Odigeo'] && window['Odigeo'].AB.isPreSearchActive
        && window.location.hash.includes('pax')) {
        const oldFormat = SearchService.toOldFormat(state);
        const searchURL = SearchService.getSearchURL({
            ...state,
            activeProduct: window['Odigeo'].SessionScope.flowProduct,
        });
        PreSearchUtils.preSearch(oldFormat, searchURL.split('#')[1]);
    }
    return { ...state };
};

const updateNonPrimeHotelRoom = (state, { room }) => {
    return {
        ...state,
        nonPrimeHotelRooms: { ...room },
    };
};

const updateLastKnownOrigin = (state) => ({
    ...state,
    lastKnownOrigin: state.segments[0].origin,
});

const updateSearcherConfig = (state, { config }) => (
    {
        ...state,
        config: {
            ...state.config,
            ...config
        }
    }
);

export default function(state = initialState, action, store) {
    const reducer = {
        [CurrentSearch.ADD_SEGMENT]: () => addSegment(state, action),
        [CurrentSearch.DELETE_SEGMENT]: () => deleteSegment(state, action),
        [CurrentSearch.UPDATE_SEGMENT]: () => updateSegment(state, action, store),
        [CurrentSearch.RESET_SEARCH]: () => resetSearch(state),
        [CurrentSearch.SET_ALL_SEARCH]: () => setAllSearch(state, action),
        [CurrentSearch.SET_CABIN_CLASS]: () => setCabinClass(state, action),
        [CurrentSearch.SET_CHAIN_FILTER]: () => setChainFilter(state, action),
        [CurrentSearch.SET_HOTEL]: () => setHotel(state, action),
        [CurrentSearch.SET_ONLY_DIRECT_FLIGHTS]: () => setOnlyDirectFlights(state, action),
        [CurrentSearch.SET_AIRLINE_FILTER]: () => setAirlineFilter(state, action),
        [CurrentSearch.SET_PAX]: () => setPax(state, action),
        [CurrentSearch.ADD_ROOM]: () => addRoom(state, action),
        [CurrentSearch.UPDATE_ROOM]: () => updateRoom(state, action),
        [CurrentSearch.DELETE_ROOM]: () => deleteRoom(state, action),
        [CurrentSearch.SET_RESIDENT_DISCOUNT]: () => setResidentDiscount(state, action),
        [CurrentSearch.SET_TRIP_TYPE]: () => setTripType(state, action),
        [CurrentSearch.SET_HOTEL_TRIP_TYPE]: () => setHotelTripType(state, action),
        [CurrentSearch.SET_SHOW_ERRORS]: () => setShowErrors(state, action),
        [CurrentSearch.SET_NON_PRIME_HOTEL_SHOW_ERRORS]: () => setNonPrimeHotelShowErrors(state, action),
        [CurrentSearch.SET_SHOW_ROOM_ERRORS]: () => setShowRoomErrors(state, action),
        [CurrentSearch.SET_SEPARATE_STAY]: () => setSeparateStay(state, action),
        [CurrentSearch.HANDLE_PRE_SEARCH]: () => handlePreSearch(state),
        [CurrentSearch.UPDATE_NON_PRIME_HOTEL_ROOM]: () => updateNonPrimeHotelRoom(state, action),
        [CurrentSearch.UPDATE_CONFIG]: () => updateSearcherConfig(state, action),
    }[action.type];

    return reducer ? reducer() : state;
}
